'use client';

import { useEffect, useRef } from 'react';
import { logError } from 'commons/logger';
import isSyndecaExcludedPath from '@/utils/isSyndecaExcludedPath';

// Syndeca's universal loader — migrate to
// the pinned npm package (@syndeca/syndeca-universal-loader) once they grant
// access.
const LOADER_URL = 'https://cdn.syndeca.com/universal.js';
const DEFAULT_ACCOUNT = 'joybird';
const LOAD_DEBOUNCE_MS = 250;
const getAccount = () => globalThis._env_?.NEXT_PUBLIC_SYNDECA_ACCOUNT || DEFAULT_ACCOUNT;

// Elements the loader itself adds: the wrapper it puts around each shoppable
// image, its hotspot anchors and its tooltip. Ignoring them stops load() from
// re-triggering itself in a loop via our own MutationObserver.
const isSyndecaNode = node => node.nodeType === Node.ELEMENT_NODE && (node.classList?.contains('syndeca-shoppable-image') || !!node.id?.startsWith('syndeca-'));
const containsImage = node => node.nodeType === Node.ELEMENT_NODE && (node.tagName === 'IMG' || !!node.querySelector?.('img'));
const hasAddedImage = mutations => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (containsImage(node) && !isSyndecaNode(node)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Loads Syndeca Shop Layer, which turns images across the site into shoppable
 * images. Hotspot clicks are dispatched as a `syndecaProductClick` CustomEvent
 * on `document` and picked up by useQuickViewModalState, which opens our own
 * QuickView modal — the same modal the lookbook/catalog viewer already uses.
 */
const useSyndecaShopLayer = () => {
  const instanceRef = useRef(null);
  const importRef = useRef(null);
  useEffect(() => {
    const ensureInstance = async () => {
      if (!importRef.current) {
        // Must be a string literal for the bundler magic comments above to
        // apply — keep it in sync with LOADER_URL.
        importRef.current = import(/* webpackIgnore: true */
        /* turbopackIgnore: true */
        // eslint-disable-next-line import/no-unresolved -- remote module, resolved by the browser at runtime
        'https://cdn.syndeca.com/universal.js').catch(error => {
          importRef.current = null;
          throw error;
        });
      }
      const {
        SyndecaShoppableImage
      } = await importRef.current;
      if (!instanceRef.current) {
        instanceRef.current = new SyndecaShoppableImage({
          account: getAccount(),
          autoload: false
        });
      }
      return instanceRef.current;
    };
    const loadIfAllowed = async () => {
      if (isSyndecaExcludedPath(window.location.pathname)) {
        return;
      }
      try {
        const instance = await ensureInstance();

        // Re-check: the import above is a network round trip, and the shopper
        // can soft-navigate onto an excluded route while it is in flight.
        if (isSyndecaExcludedPath(window.location.pathname)) {
          return;
        }
        instance?.load();
      } catch (error_0) {
        logError(error_0, {
          message: `Error loading Syndeca Shop Layer from ${LOADER_URL}`
        });
      }
    };
    let timeoutId;
    const scheduleLoad = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(loadIfAllowed, LOAD_DEBOUNCE_MS);
    };
    scheduleLoad();
    const observer = new MutationObserver(mutations => {
      if (hasAddedImage(mutations)) {
        scheduleLoad();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);
};
export default useSyndecaShopLayer;